import React, { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import './AICampaigns.css';

const AICampaigns = () => {
  // Refs for animation elements
  const howItWorksRef = useRef(null);
  const processItemsRef = useRef([]);

  // Scroll to top when component mounts and setup animations
  useEffect(() => {
    window.scrollTo(0, 0);

    // Initialize Intersection Observer for animations
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.15,
    };

    const handleIntersect = (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // Add animation class when element is in view
          entry.target.classList.add('aicamp_visible');
          observer.unobserve(entry.target); // Stop observing once animated
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersect, observerOptions);

    // Observe section heading
    if (howItWorksRef.current) {
      const heading = howItWorksRef.current.querySelector('.section-heading h2');
      const subheading = howItWorksRef.current.querySelector('.section-heading p');

      if (heading) observer.observe(heading);
      if (subheading) observer.observe(subheading);
    }

    // Observe all process items
    const animatedElements = document.querySelectorAll('.aicamp_animate');
    animatedElements.forEach((element) => {
      observer.observe(element);
    });

    return () => {
      // Cleanup observer on component unmount
      observer.disconnect();
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Best AI Campaign Optimization for Smarter Marketing and Conversion tracking</title>
        <meta
          name="description"
          content="Smart AI driven marketing and advertising campaign optimization provides real-time performance metrics and automated alerts for campaign milestones."
        />
        <meta
          name="keywords"
          content="AI Campaign Optimization for Smarter Marketing, AI Campaign Manager, ai for marketing campaigns, marketing campaigns with AI, artificial intelligence advertising campaign"
        />
        <link rel="canonical" href="https://www.aichatassist.com/features/ai-campaigns" />

        {/* Open Graph Meta */}
        <meta
          property="og:title"
          content="Smarter AI Campaigns | AI Chat Assist for Global Marketing"
        />
        <meta
          property="og:description"
          content="AI Chat Assist powers AI campaign optimization in Singapore, UK & UAE. Engage customers effectively with intelligent, data-driven marketing tools."
        />
        <meta property="og:url" content="https://www.aichatassist.com/features/ai-campaigns" />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="https://admin.aichatassist.com/uploads/Wordpress_Plugin_Banner_6964048f39.jpg"
        />
        <meta property="og:site_name" content="AI Chat Assist" />
        <meta property="og:locale" content="en_US" />

        {/* Twitter Meta */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Smarter AI Campaigns | AI Chat Assist for Global Marketing"
        />
        <meta
          name="twitter:description"
          content="AI Chat Assist powers AI campaign optimization in Singapore, UK & UAE. Engage customers effectively with intelligent, data-driven marketing tools."
        />
        <meta
          name="twitter:image"
          content="https://admin.aichatassist.com/uploads/Wordpress_Plugin_Banner_6964048f39.jpg"
        />
        <meta name="twitter:site" content="@aichatassist" />
      </Helmet>

      {/* Hero Section */}
      <section className="page-hero-section py-5">
        <div className="container position-relative" style={{ zIndex: 2 }}>
          <div className="row align-items-center">
            <div className="col-lg-12 text-center text-lg-start">
              <div
                className="d-inline-flex align-items-center bg-dark text-white px-3 py-2 rounded-pill mb-4 hero-badge"
                style={{ background: `var(--purple-gradient)` }}
              >
                <span className="small">Smarter AI Campaign Management</span>
              </div>

              <h1 className="hero-heading mb-4 animate-up">
                <span className="">
                  <span className="hero-highlight">AI Campaign</span> Optimization
                </span>
                <br />
                <span>for Smarter Marketing</span>
              </h1>

              <p className="lead mb-5 text-center">
                Run smarter marketing campaigns with AI-powered automation, targeting, and tracking.
              </p>

              <div className="container-fluid px-0 mt-4">
                <img
                  src="/images/aicampaign.svg"
                  alt="AI Campaign Network"
                  className="img-fluid animate-scale w-100"
                  style={{ borderRadius: 0 }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features Section */}
      <section id="features" className="ai_features_section py-5">
        <div className="container">
          <div className="ai_section_heading text-center mb-5">
            <h2>
              What We <span className="hero-highlight">Offer</span>
            </h2>
            <p>Powerful capabilities to transform your marketing campaigns</p>
          </div>

          {/* SVG Gradient Definition */}
          <svg width="0" height="0" className="d-none">
            <defs>
              <linearGradient id="purple-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#8c6fff" />
                <stop offset="100%" stopColor="#ce75ff" />
              </linearGradient>
              <linearGradient id="card-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#8c6fff" />
                <stop offset="100%" stopColor="#ce75ff" />
              </linearGradient>
            </defs>
          </svg>
          <div className="ai_carousel_container">
            <Slider
              className="ai_features_carousel"
              dots={true}
              infinite={true}
              speed={500}
              slidesToShow={4}
              slidesToScroll={1}
              autoplay={true}
              autoplaySpeed={2000}
              pauseOnHover={true}
              pauseOnFocus={true}
              swipeToSlide={true}
              centerMode={false}
              responsive={[
                {
                  breakpoint: 1400,
                  settings: {
                    slidesToShow: 3,
                    slidesToScroll: 1,
                  },
                },
                {
                  breakpoint: 1024,
                  settings: {
                    slidesToShow: 2,
                    slidesToScroll: 1,
                    dots: true,
                  },
                },
                {
                  breakpoint: 768,
                  settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1,
                  },
                },
              ]}
            >
              <div className="ai_carousel_item">
                <div className="ai_feature_card ai_card_primary">
                  <div className="ai_feature_icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                      <circle cx="9" cy="7" r="4"></circle>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                    </svg>
                  </div>
                  <h3 className="ai_feature_title">Smart Targeting with AI</h3>
                  <p className="ai_feature_text">
                    Our AI Campaign Manager evaluates product data, trends, and audience behavior to
                    pinpoint segments that convert best. Run marketing campaigns with AI precision.
                  </p>
                  {/* <button className="ai_join_btn">Join Now</button> */}
                </div>
              </div>

              <div className="ai_carousel_item">
                <div className="ai_feature_card ai_card_secondary">
                  <div className="ai_feature_icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                      <line x1="3" y1="9" x2="21" y2="9"></line>
                      <line x1="9" y1="21" x2="9" y2="9"></line>
                    </svg>
                  </div>
                  <h3 className="ai_feature_title">AI Campaign Optimization</h3>
                  <p className="ai_feature_text">
                    Improve performance with AI for marketing campaigns. Get real-time insights to
                    adjust creatives, budgets, and strategies for smarter advertising.
                  </p>
                  {/* <button className="ai_join_btn">Join Now</button> */}
                </div>
              </div>

              <div className="ai_carousel_item">
                <div className="ai_feature_card ai_card_secondary">
                  <div className="ai_feature_icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                      <line x1="8" y1="21" x2="16" y2="21"></line>
                      <line x1="12" y1="17" x2="12" y2="21"></line>
                      <path d="M6 8h.01M9 8h.01"></path>
                      <polyline points="15 8 18 8 18 11"></polyline>
                      <path d="M18 8l-3 3-3-3-3 3-3-3"></path>
                    </svg>
                  </div>
                  <h3 className="ai_feature_title">Campaign Performance Reports</h3>
                  <p className="ai_feature_text">
                    Track KPIs like clicks, ROI, and conversions across platforms. Use AI campaign
                    optimization for smarter marketing decisions and better results.
                  </p>
                  {/* <button className="ai_join_btn">Join Now</button> */}
                </div>
              </div>

              <div className="ai_carousel_item">
                <div className="ai_feature_card ai_card_tertiary">
                  <div className="ai_feature_icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                      <polyline points="22,6 12,13 2,6"></polyline>
                    </svg>
                  </div>
                  <h3 className="ai_feature_title">AI Retargeting & Lookalikes</h3>
                  <p className="ai_feature_text">
                    Re-engage visitors and expand your reach using artificial intelligence
                    advertising campaigns that automate retargeting and build high-converting
                    lookalike audiences.
                  </p>
                  {/* <button className="ai_join_btn">Join Now</button> */}
                </div>
              </div>

              <div className="ai_carousel_item">
                <div className="ai_feature_card ai_card_tertiary">
                  <div className="ai_feature_icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                    </svg>
                  </div>
                  <h3 className="ai_feature_title">Easy UTM Tracking</h3>
                  <p className="ai_feature_text">
                    Track every click with simple UTM setup. Know exactly which marketing campaigns
                    with AI are driving traffic and conversions.
                  </p>
                  {/* <button className="ai_join_btn">Join Now</button> */}
                </div>
              </div>

              <div className="ai_carousel_item">
                <div className="ai_feature_card ai_card_primary">
                  <div className="ai_feature_icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                      <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                      <line x1="12" y1="22.08" x2="12" y2="12"></line>
                    </svg>
                  </div>
                  <h3 className="ai_feature_title">Conversion Tracking Made Easy</h3>
                  <p className="ai_feature_text">
                    Set up and track conversions across ads and pages with help from your AI
                    Campaign Manager. No tech skills needed—just accurate, useful results.
                  </p>
                  {/* <button className="ai_join_btn">Join Now</button> */}
                </div>
              </div>
            </Slider>
          </div>
        </div>
      </section>

      {/* How It Works Section - Redesigned to match the provided image */}
      <section className="how-it-works-section py-5" ref={howItWorksRef}>
        <div className="container">
          <div className="section-heading text-center mb-5">
            <h2 className="aicamp_animate aicamp_up">
              How It <span className="hero-highlight">Works</span>
            </h2>
            <p className="aicamp_animate aicamp_up aicamp_delay_1">
              Simple steps to transform your marketing campaigns with AI
            </p>
          </div>

          <div className="process-container">
            {/* Step 1 - Image on left, content on right */}
            <div className="process-item" ref={(el) => (processItemsRef.current[0] = el)}>
              <div className="process-left">
                <div className="step-image-container">
                  <div className="step-image-bg"></div>
                  <div className="step-image">
                    <div className="aicamp_animate aicamp_scale">
                      <svg
                        width="150"
                        height="150"
                        viewBox="0 0 200 200"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <defs>
                          <linearGradient id="purple-gradient-1" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#8c6fff" />
                            <stop offset="100%" stopColor="#ce75ff" />
                          </linearGradient>
                        </defs>
                        <rect
                          x="40"
                          y="60"
                          width="120"
                          height="80"
                          rx="8"
                          fill="white"
                          stroke="url(#purple-gradient-1)"
                          strokeWidth="3"
                        />
                        <rect x="50" y="70" width="100" height="15" rx="4" fill="#f5f3ff" />
                        <rect
                          x="50"
                          y="95"
                          width="40"
                          height="35"
                          rx="4"
                          fill="url(#purple-gradient-1)"
                          opacity="0.7"
                        />
                        <rect
                          x="100"
                          y="95"
                          width="40"
                          height="35"
                          rx="4"
                          fill="url(#purple-gradient-1)"
                          opacity="0.4"
                        />
                        <path
                          d="M65 82L75 82"
                          stroke="url(#purple-gradient-1)"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <path
                          d="M85 82L125 82"
                          stroke="#e6e6e6"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <circle cx="60" cy="112" r="5" fill="white" />
                        <circle cx="120" cy="112" r="5" fill="white" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
              <div className="process-right">
                <div className="step-tag aicamp_animate aicamp_right aicamp_delay_1">1</div>
                <div className="step-content">
                  <h3 className="aicamp_animate aicamp_right aicamp_delay_2">
                    Connect Your Ad Account
                  </h3>
                  <p className="aicamp_animate aicamp_right aicamp_delay_3">
                    Link platforms like Google or Meta to get started. Our AI Campaign Manager
                    ensures a smooth, secure setup to begin optimizing campaigns right away.
                  </p>
                </div>
              </div>
            </div>

            {/* Step 2 - Content on left, image on right */}
            <div className="process-item" ref={(el) => (processItemsRef.current[1] = el)}>
              <div className="process-left">
                <div className="step-tag aicamp_animate aicamp_left aicamp_delay_1">2</div>
                <div className="step-content">
                  <h3 className="aicamp_animate aicamp_left aicamp_delay_2">
                    Set Campaign Goals with AI
                  </h3>
                  <p className="aicamp_animate aicamp_left aicamp_delay_3">
                    Define audience details, budget, goals, and targeting to get started. Our AI
                    Campaign Optimization for smarter marketing aligns this info to tailor impactful
                    ad strategies using AI.
                  </p>
                </div>
              </div>
              <div className="process-right">
                <div className="step-image-container">
                  <div className="step-image-bg"></div>
                  <div className="step-image">
                    <div className="aicamp_animate aicamp_scale aicamp_delay_2">
                      <svg
                        width="150"
                        height="150"
                        viewBox="0 0 200 200"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <defs>
                          <linearGradient id="purple-gradient-2" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#8c6fff" />
                            <stop offset="100%" stopColor="#ce75ff" />
                          </linearGradient>
                        </defs>
                        <rect
                          x="50"
                          y="50"
                          width="100"
                          height="100"
                          rx="8"
                          fill="white"
                          stroke="url(#purple-gradient-2)"
                          strokeWidth="3"
                        />
                        <rect
                          x="60"
                          y="60"
                          width="80"
                          height="10"
                          rx="2"
                          fill="url(#purple-gradient-2)"
                          opacity="0.2"
                        />
                        <rect
                          x="60"
                          y="80"
                          width="80"
                          height="10"
                          rx="2"
                          fill="url(#purple-gradient-2)"
                          opacity="0.2"
                        />
                        <rect
                          x="60"
                          y="100"
                          width="80"
                          height="10"
                          rx="2"
                          fill="url(#purple-gradient-2)"
                          opacity="0.2"
                        />
                        <rect
                          x="60"
                          y="120"
                          width="80"
                          height="10"
                          rx="2"
                          fill="url(#purple-gradient-2)"
                          opacity="0.2"
                        />
                        <circle cx="70" cy="65" r="3" fill="url(#purple-gradient-2)" />
                        <circle cx="70" cy="85" r="3" fill="url(#purple-gradient-2)" />
                        <circle cx="70" cy="105" r="3" fill="url(#purple-gradient-2)" />
                        <circle cx="70" cy="125" r="3" fill="url(#purple-gradient-2)" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 3 - Image on left, content on right */}
            <div className="process-item" ref={(el) => (processItemsRef.current[2] = el)}>
              <div className="process-left">
                <div className="step-image-container">
                  <div className="step-image-bg"></div>
                  <div className="step-image">
                    <div className="aicamp_animate aicamp_scale">
                      <svg
                        width="150"
                        height="150"
                        viewBox="0 0 200 200"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <defs>
                          <linearGradient id="purple-gradient-3" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#8c6fff" />
                            <stop offset="100%" stopColor="#ce75ff" />
                          </linearGradient>
                        </defs>
                        <circle
                          cx="100"
                          cy="100"
                          r="50"
                          fill="white"
                          stroke="url(#purple-gradient-3)"
                          strokeWidth="3"
                        />
                        <path
                          d="M100 60V100L130 115"
                          stroke="url(#purple-gradient-3)"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M160 100C160 133.137 133.137 160 100 160C66.8629 160 40 133.137 40 100C40 66.8629 66.8629 40 100 40"
                          stroke="url(#purple-gradient-3)"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeDasharray="6 6"
                        />
                        <circle cx="100" cy="100" r="5" fill="url(#purple-gradient-3)" />
                        <circle
                          cx="160"
                          cy="40"
                          r="10"
                          fill="url(#purple-gradient-3)"
                          opacity="0.3"
                        />
                        <circle
                          cx="40"
                          cy="160"
                          r="10"
                          fill="url(#purple-gradient-3)"
                          opacity="0.3"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
              <div className="process-right">
                <div className="step-tag aicamp_animate aicamp_right aicamp_delay_1">3</div>
                <div className="step-content">
                  <h3 className="aicamp_animate aicamp_right aicamp_delay_2">
                    Get Smart Insights from AI
                  </h3>
                  <p className="aicamp_animate aicamp_right aicamp_delay_3">
                    Our AI Campaign Manager studies top-performing campaigns to suggest what works
                    best for your brand. It’s intelligent campaign optimization made simple with
                    machine learning.
                  </p>
                </div>
              </div>
            </div>

            {/* Step 4 - Content on left, image on right */}
            <div className="process-item" ref={(el) => (processItemsRef.current[3] = el)}>
              <div className="process-left">
                <div className="step-tag aicamp_animate aicamp_left aicamp_delay_1">4</div>
                <div className="step-content">
                  <h3 className="aicamp_animate aicamp_left aicamp_delay_2">
                    Launch & Monitor with AI Precision
                  </h3>
                  <p className="aicamp_animate aicamp_left aicamp_delay_3">
                    Start your AI-optimized marketing campaigns instantly. Monitor real-time
                    performance and get automated alerts as your AI Campaign Manager tracks every
                    milestone.
                  </p>
                </div>
              </div>
              <div className="process-right">
                <div className="step-image-container">
                  <div className="step-image-bg"></div>
                  <div className="step-image">
                    <div className="aicamp_animate aicamp_scale aicamp_delay_2">
                      <svg
                        width="150"
                        height="150"
                        viewBox="0 0 200 200"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <defs>
                          <linearGradient id="purple-gradient-4" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#8c6fff" />
                            <stop offset="100%" stopColor="#ce75ff" />
                          </linearGradient>
                        </defs>
                        <rect
                          x="40"
                          y="70"
                          width="120"
                          height="80"
                          rx="4"
                          fill="white"
                          stroke="url(#purple-gradient-4)"
                          strokeWidth="3"
                        />
                        <rect x="50" y="80" width="100" height="50" rx="2" fill="#f5f3ff" />
                        <path
                          d="M60 100L80 120L100 90L120 110L140 95"
                          stroke="url(#purple-gradient-4)"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <circle cx="80" cy="120" r="4" fill="url(#purple-gradient-4)" />
                        <circle cx="100" cy="90" r="4" fill="url(#purple-gradient-4)" />
                        <circle cx="120" cy="110" r="4" fill="url(#purple-gradient-4)" />
                        <rect
                          x="50"
                          y="140"
                          width="30"
                          height="5"
                          rx="2.5"
                          fill="url(#purple-gradient-4)"
                          opacity="0.5"
                        />
                        <rect
                          x="90"
                          y="140"
                          width="30"
                          height="5"
                          rx="2.5"
                          fill="url(#purple-gradient-4)"
                          opacity="0.5"
                        />
                        <rect
                          x="130"
                          y="140"
                          width="20"
                          height="5"
                          rx="2.5"
                          fill="url(#purple-gradient-4)"
                          opacity="0.5"
                        />
                        <circle
                          cx="170"
                          cy="50"
                          r="10"
                          fill="url(#purple-gradient-4)"
                          opacity="0.2"
                        />
                        <circle
                          cx="30"
                          cy="150"
                          r="10"
                          fill="url(#purple-gradient-4)"
                          opacity="0.2"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 5 - Image on left, content on right */}
            <div className="process-item" ref={(el) => (processItemsRef.current[4] = el)}>
              <div className="process-left">
                <div className="step-image-container">
                  <div className="step-image-bg"></div>
                  <div className="step-image">
                    <div className="aicamp_animate aicamp_scale">
                      <svg
                        width="150"
                        height="150"
                        viewBox="0 0 200 200"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <defs>
                          <linearGradient id="purple-gradient-5" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#8c6fff" />
                            <stop offset="100%" stopColor="#ce75ff" />
                          </linearGradient>
                        </defs>
                        <circle
                          cx="100"
                          cy="100"
                          r="60"
                          fill="white"
                          stroke="url(#purple-gradient-5)"
                          strokeWidth="3"
                        />
                        <path
                          d="M70 100C70 83.4315 83.4315 70 100 70C116.569 70 130 83.4315 130 100C130 116.569 116.569 130 100 130"
                          stroke="url(#purple-gradient-5)"
                          strokeWidth="3"
                          strokeLinecap="round"
                        />
                        <path
                          d="M100 130C91.7157 130 85 123.284 85 115C85 106.716 91.7157 100 100 100"
                          stroke="url(#purple-gradient-5)"
                          strokeWidth="3"
                          strokeLinecap="round"
                        />
                        <circle cx="100" cy="100" r="5" fill="url(#purple-gradient-5)" />
                        <path
                          d="M140 60L150 70L140 80"
                          stroke="url(#purple-gradient-5)"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M60 120L50 130L60 140"
                          stroke="url(#purple-gradient-5)"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <circle
                          cx="150"
                          cy="70"
                          r="4"
                          fill="url(#purple-gradient-5)"
                          opacity="0.5"
                        />
                        <circle
                          cx="50"
                          cy="130"
                          r="4"
                          fill="url(#purple-gradient-5)"
                          opacity="0.5"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
              <div className="process-right">
                <div className="step-tag aicamp_animate aicamp_right aicamp_delay_1">5</div>
                <div className="step-content">
                  <h3 className="aicamp_animate aicamp_right aicamp_delay_2">
                    Refine Campaigns with AI Learning
                  </h3>
                  <p className="aicamp_animate aicamp_right aicamp_delay_3">
                    AI continuously learns from campaign results to improve performance. Each update
                    fine-tunes your strategy, making marketing campaigns with AI more effective over
                    time.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose AI Chat Assist Section */}

      {/* Campaign Analytics Section */}
      <section className="analytics-section py-5">
        <div className="container">
          <div className="text-center aicampaign mb-0">
            <h2 className="section-title aicamp_animate aicamp_up">
              AI Campaign Analytics for <span className="highlight">Smarter Marketing</span>
            </h2>
            <p className="section-subtitle aicamp_animate aicamp_up">
              Unlock powerful insights with AI-driven campaign analytics. Go beyond basic metrics to
              track performance, optimize engagement, and enhance your marketing campaigns with AI.
            </p>
          </div>

          <div className="analytics-dashboard">
            {/* <div className="dashboard-image">
              <img
                src="/images/features/campaign-analytics.png"
                alt="Campaign Analytics Dashboard"
                className="img-fluid rounded shadow-lg"
              />
            </div> */}

            <div className="feature-cards-container">
              <div className="feature-grid aicamp_animate aicamp_up">
                <div className="analytics-card">
                  <div className="analytics-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M3 3v18h18"></path>
                      <path d="m19 9-5 5-4-4-3 3"></path>
                    </svg>
                  </div>
                  <h3>Real-time Tracking</h3>
                  <p>
                    Track campaign performance in real time with AI insights and alerts that keep
                    marketing aligned with goals.
                  </p>
                </div>

                <div className="analytics-card">
                  <div className="analytics-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M12 20V10"></path>
                      <path d="M18 20V4"></path>
                      <path d="M6 20v-4"></path>
                    </svg>
                  </div>
                  <h3>Engagement Metrics</h3>
                  <p>
                    Analyze audience behavior, click-through rates, and interactions to improve
                    marketing campaigns with AI.
                  </p>
                </div>

                <div className="analytics-card">
                  <div className="analytics-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"></path>
                      <line x1="4" y1="22" x2="4" y2="15"></line>
                    </svg>
                  </div>
                  <h3>Journey Visualization</h3>
                  <p>
                    Understand the complete customer journey with AI campaign tracking—from first
                    interaction to conversion.
                  </p>
                </div>

                <div className="analytics-card">
                  <div className="analytics-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21 11V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h6"></path>
                      <path d="m12 12 4 10 1.7-4.3L22 16Z"></path>
                    </svg>
                  </div>
                  <h3>AI Insights</h3>
                  <p>
                    Gain insights that refine campaign strategies using data from successful
                    artificial intelligence advertising campaigns.
                  </p>
                </div>

                <div className="analytics-card">
                  <div className="analytics-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                      <line x1="8" y1="21" x2="16" y2="21"></line>
                      <line x1="12" y1="17" x2="12" y2="21"></line>
                    </svg>
                  </div>
                  <h3>Cross-Platform Data</h3>
                  <p>
                    Combine analytics across all ad platforms. Use AI to streamline campaign
                    monitoring and decision-making.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Integration Section */}
      {/* <section className="integration-section py-5">
        <div className="container">
          <div className="section-heading text-center mb-5">
            <h2>Seamless <span className="highlight">Integrations</span></h2>
            <p>Connect with your existing marketing stack</p>
          </div>

          <div className="row g-4 justify-content-center">
            <div className="col-6 col-md-4 col-lg-2">
              <div className="integration-logo">
                <img src="/images/integrations/mailchimp.png" alt="Mailchimp" />
                <p>Mailchimp</p>
              </div>
            </div>
            <div className="col-6 col-md-4 col-lg-2">
              <div className="integration-logo">
                <img src="/images/integrations/hubspot.png" alt="HubSpot" />
                <p>HubSpot</p>
              </div>
            </div>
            <div className="col-6 col-md-4 col-lg-2">
              <div className="integration-logo">
                <img src="/images/integrations/salesforce.png" alt="Salesforce" />
                <p>Salesforce</p>
              </div>
            </div>
            <div className="col-6 col-md-4 col-lg-2">
              <div className="integration-logo">
                <img src="/images/integrations/marketo.png" alt="Marketo" />
                <p>Marketo</p>
              </div>
            </div>
            <div className="col-6 col-md-4 col-lg-2">
              <div className="integration-logo">
                <img src="/images/integrations/shopify.png" alt="Shopify" />
                <p>Shopify</p>
              </div>
            </div>
            <div className="col-6 col-md-4 col-lg-2">
              <div className="integration-logo">
                <img src="/images/integrations/zapier.png" alt="Zapier" />
                <p>Zapier</p>
              </div>
            </div>
          </div>

          <div className="text-center mt-4">
            <Link to="/platform/crm-api-integration" className="btn btn-outline-primary">View All Integrations</Link>
          </div>
        </div>
      </section> */}
    </>
  );
};

export default AICampaigns;
